import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form, Select, DatePicker, Typography, Space, Card, Button, Avatar, Input } from 'antd'
import { PlayCircleOutlined } from '@ant-design/icons'
import { LightningIcon, TimerIcon, GearsIcon } from '../../icons'
import { StepComponentProps, OnboardingV2Data } from '../index'
import dayjs from 'dayjs'
import IntlMessages from '../../../util/IntlMessages'
import Api from '@vacationtracker/shared/services/api'
import { useOnboardingContext, useMarkStepCompleted } from '../context'
import logger from '@vacationtracker/shared/functions/logger'
import { track } from '../../../services/analytics/analytics'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { selectAuthUserSlice } from '../../../store/auth-user-slice'
import { useManualQuery } from 'graphql-hooks'
import { getLeaveTypesList } from '../../../graphql/custom-queries'
import useDebounce from '../../../util/use-debounce'
import { useHistory } from 'react-router-dom'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

export const LeaveRequestStep: React.FC<StepComponentProps<OnboardingV2Data>> = ({
  onNext,
  errors,
  onClose,
}) => {
  const history = useHistory()
  const { dispatch: updateData, isLoading, formData: data } = useOnboardingContext()
  const markStepCompleted = useMarkStepCompleted()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser: latestAuthUser } = useAppSelector(selectAuthUserSlice)
  const [fetchLeaveTypes] = useManualQuery<any>(getLeaveTypesList)
  const [activeLeaveTypeIds, setActiveLeaveTypeIds] = useState<Set<string>>(new Set())
  const [activeLeaveTypeIdsForLocation, setActiveLeaveTypeIdsForLocation] = useState<Set<string>>(new Set())
  const [reasonInput, setReasonInput] = useState<string>(data.leaveReason || '')
  const hasTrackedShownRef = useRef(false)

  // Debounced tracking for edited fields (Step 4)
  const [lastEdited, setLastEdited] = useState<{ field_name: string; new_value: string } | null>(null)
  const debouncedEdited = useDebounce(lastEdited, 1000)

  useEffect(() => {
    if (!authCompany || !debouncedEdited) return
    track('ONBOARDING_STEP_4_EDITED_FIELDS', {
      field_name: debouncedEdited.field_name,
      new_value: debouncedEdited.new_value,
      company_id: authCompany.id,
    })
  }, [debouncedEdited, authCompany])

  // Keep local reason in sync if context changes externally
  useEffect(() => {
    setReasonInput(data.leaveReason || '')
  }, [data.leaveReason])

  // Load global leave types to get latest isActive flags even if authUser policies are stale
  useEffect(() => {
    let isMounted = true
    ;(async () => {
      try {
        const res = await fetchLeaveTypes({ skipCache: true })
        const list = res?.data?.getLeaveTypeList || []
        if (isMounted) {
          const globalActive = new Set<string>(list.filter((lt: any) => lt?.isActive).map((lt: any) => lt.id))
          setActiveLeaveTypeIds(globalActive)

          const locationId = latestAuthUser?.location?.id
          if (locationId) {
            const forLocation = new Set<string>(
              list
                .filter((lt: any) => Array.isArray(lt?.leavePolicies))
                .filter((lt: any) => lt.leavePolicies.some((p: any) => p?.location?.id === locationId && p?.isActive))
                .map((lt: any) => lt.id)
            )
            setActiveLeaveTypeIdsForLocation(forLocation)
          } else {
            setActiveLeaveTypeIdsForLocation(new Set())
          }
        }
      } catch {
        // noop: fall back to policy.leaveType.isActive only
      }
    })()
    return () => {
      isMounted = false
    }
  }, [fetchLeaveTypes, latestAuthUser?.location?.id])

  // Seed location-specific active set from latest authUser immediately to avoid brief stale window
  useEffect(() => {
    const policies = latestAuthUser?.location?.leavePolicies || []
    const activeIds = new Set<string>(
      policies
        .filter(p => p?.leaveType?.id && p?.isActive && p?.leaveType?.isActive)
        .map(p => p.leaveType!.id)
    )
    setActiveLeaveTypeIdsForLocation(activeIds)
  }, [latestAuthUser?.location?.leavePolicies])

  // Track "shown" once on initial render when data is available
  useEffect(() => {
    if (hasTrackedShownRef.current) return
    if (authCompany && data?.authUser) {
      track('ONBOARDING_STEP_4_TRY_IT_OUT_SHOWN', {
        company_id: authCompany.id,
        user_id: data.authUser.id,
      })
      hasTrackedShownRef.current = true
    }
  }, [authCompany, data?.authUser])

  // Pre-fill form data on initial render
  useEffect(() => {
    if (!data.leaveType && !data.leaveStartDate && !data.leaveEndDate && !data.leaveReason) {
      const effectiveLeavePolicies: any = (data.leavePolicies && data.leavePolicies.length > 0)
        ? data.leavePolicies
        : (latestAuthUser?.location?.leavePolicies || [])

      const activePolicies = effectiveLeavePolicies.filter(p => p?.leaveType?.isActive)
      if (activePolicies.length > 0) {
        // Prefer PTO if present; otherwise pick first active
        const preferred = activePolicies.find(policy => policy.leaveType?.name === 'Paid Time Off') || activePolicies[0]
        if (preferred?.leaveType?.id) {
          const today = dayjs().format('YYYY-MM-DD')
          updateData({
            type: 'UPDATE_FORM_DATA',
            payload: {
              leaveType: preferred.leaveType.id,
              leaveStartDate: today,
              leaveEndDate: today,
              leaveReason: 'Testing, will delete later',
            },
          })
        }
      }
    }
  }, [data.leaveStartDate, data.leaveEndDate, data.leaveReason, updateData])

  const handleNext = useCallback(async () => {
    if (!data.leaveType || !data.leaveStartDate || !data.leaveEndDate || !onNext || !data.authUser) return

    try {
      updateData({ type: 'SET_LOADING', payload: true })

      if (authCompany) {
        track('ONBOARDING_STEP_4_SUBMITTED_REQUEST', {
          leave_type: data.leaveType,
          start_date: data.leaveStartDate,
          end_date: data.leaveEndDate,
          company_id: authCompany.id,
        })
      }

      // Use a different API endpoint that doesn't trigger global events
      // or add a flag to prevent dashboard from reacting to this event
      await Api.post('/core/event', {
        eventType: 'LEAVE_REQUEST_CREATED',
        eventGroup: 'USER_LEAVE_REQUEST',
        leaveTypeId: data.leaveType,
        userId: data.authUser.id,
        startDate: data.leaveStartDate,
        endDate: data.leaveEndDate,
        isPartDay: false,
        reason: data.leaveReason || 'Testing, will delete later',
        // Add a flag to indicate this is from onboarding
        isOnboarding: true,
      })

      // Mark step as completed (track relevant steps only)
      markStepCompleted('leave-request')

      await onNext()
    } catch (error) {
      logger.error('Error creating leave request:', error)
      updateData({
        type: 'SET_ERRORS',
        payload: { leaveRequest: 'Failed to create leave request. Please try again.' },
      })
    } finally {
      updateData({ type: 'SET_LOADING', payload: false })
    }
  }, [data.leaveType, data.leaveStartDate, data.leaveEndDate, data.leaveReason, onNext, data.authUser, updateData, authCompany, markStepCompleted])

  const handleSkip = useCallback(() => {
    try {
      if (authCompany && data?.authUser) {
        track('ONBOARDING_STEP_SKIPPED', {
          step_id: 'leave-request',
          company_id: authCompany.id,
          user_id: data.authUser.id,
        })
      }

      // Mark this step as completed in progress storage
      markStepCompleted('leave-request')

      // Close/minimize modal first, then navigate to Users page
      onClose?.()
      setTimeout(() => {
        history.push('/app/users')
      }, 50)
    } catch {
      logger.error('Error skipping leave request step')
    }
  }, [authCompany, data?.authUser, history, markStepCompleted, onClose])

  // Get available leave types, preferring onboarding-modified policies
  const availableLeaveTypes = useMemo(() => {
    const policies: any = (data.leavePolicies && data.leavePolicies.length > 0)
      ? data.leavePolicies
      : (latestAuthUser?.location?.leavePolicies || [])

    return policies
      .filter(policy => policy?.leaveType?.id && policy?.leaveType?.name && policy?.leaveType?.isActive)
      .map(policy => policy.leaveType!)
      .filter((leaveType): leaveType is NonNullable<typeof leaveType> =>
        leaveType !== null && leaveType !== undefined &&
        typeof leaveType.id === 'string' &&
        typeof leaveType.name === 'string'
      )
      // additionally ensure the type is globally active if we fetched the list
      .filter(lt => activeLeaveTypeIds.size === 0 ? true : activeLeaveTypeIds.has(lt.id))
      // and ensure it is active for the user's location if we fetched location-specific mapping
      .filter(lt => activeLeaveTypeIdsForLocation.size === 0 ? true : activeLeaveTypeIdsForLocation.has(lt.id))
  }, [data.leavePolicies, latestAuthUser?.location?.leavePolicies, activeLeaveTypeIds, activeLeaveTypeIdsForLocation])

  // If currently selected leave type was deactivated in Location step, clear selection
  const isSelectedLeaveTypeActive = availableLeaveTypes.some(lt => lt.id === data.leaveType)
  useEffect(() => {
    if (data.leaveType && !isSelectedLeaveTypeActive) {
      updateData({ type: 'UPDATE_FORM_DATA', payload: { leaveType: undefined } })
    }
  }, [isSelectedLeaveTypeActive, data.leaveType, updateData])

  // Disable past dates based on the selected leave type's policy setting
  const selectedPolicy = ((data.leavePolicies && data.leavePolicies.length > 0)
    ? data.leavePolicies
    : (latestAuthUser?.location?.leavePolicies || []) as any)
    .find((policy) => policy?.leaveType?.id === data.leaveType)
  const allowLeaveInPast = selectedPolicy?.allowLeaveInPast ?? true

  return (
    <div className="leave-request-step">
      <div className="step-content">
        <div className="step-left-section">
          <div style={{ maxWidth: 480, margin: '0 auto', width: '100%' }}>
            <div style={{ marginBottom: 40 }}>
              <Text type="secondary" style={{ fontSize: 14, display: 'block', marginBottom: 24 }}>
                3/3
              </Text>
              <Title level={2} style={{ fontSize: 32, marginBottom: 16 }}>
                <IntlMessages id="onboarding.leaveRequest.title" />
              </Title>

              {/* User profile section */}
              {data.authUser && (
                <Card style={{ marginBottom: 24, background: '#f9f9f9', border: 'none' }} styles={{ body: { padding: 16 } }}>
                  <Space align="start" size={16}>
                    <Avatar
                      src={data.authUser.imageUrl}
                      size={40}
                      style={{ flexShrink: 0 }}
                    >
                      {data.authUser.name ? data.authUser.name.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                    <div>
                      <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                        {data.authUser.name}
                      </Text>
                      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                        {data.authUser.email}
                      </Text>
                    </div>
                  </Space>
                </Card>
              )}

              <Form layout="vertical">
                <Form.Item
                  label={<IntlMessages id="onboarding.leaveRequest.leaveTypeLabel" />}
                  required
                  validateStatus={errors?.leaveRequest ? 'error' : undefined}
                  help={errors?.leaveRequest}
                >
                  <Select
                    size="large"
                    value={data.leaveType}
                    onChange={(value) => {
                      setLastEdited({ field_name: 'leave_type', new_value: String(value) })
                      updateData({ type: 'UPDATE_FORM_DATA', payload: { leaveType: value } })
                    }}
                    style={{ maxWidth: 400 }}
                    placeholder="Select leave type"
                  >
                    {availableLeaveTypes.map(leaveType => (
                      <Select.Option key={leaveType.id} value={leaveType.id}>
                        {leaveType.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item label={<IntlMessages id="onboarding.leaveRequest.dateRangeLabel" />} required>
                  <RangePicker
                    size="large"
                    style={{ width: '100%', maxWidth: 400 }}
                    format="DD/MM/YYYY"
                    disabledDate={(current) => !allowLeaveInPast && current && current.startOf('day').isBefore(dayjs().startOf('day'))}
                    value={data.leaveStartDate && data.leaveEndDate ?
                      [dayjs(data.leaveStartDate), dayjs(data.leaveEndDate)] :
                      [dayjs(), dayjs()]
                    }
                    onChange={(dates) => {
                      if (dates) {
                        const start = dates[0]?.format('YYYY-MM-DD')
                        const end = dates[1]?.format('YYYY-MM-DD')
                        setLastEdited({ field_name: 'date_range', new_value: `${start} to ${end}` })
                        updateData({
                          type: 'UPDATE_FORM_DATA',
                          payload: {
                            leaveStartDate: dates[0]?.format('YYYY-MM-DD'),
                            leaveEndDate: dates[1]?.format('YYYY-MM-DD'),
                          },
                        })
                      }
                    }}
                  />
                </Form.Item>

                <Form.Item label={<IntlMessages id="onboarding.leaveRequest.reasonLabel" />} required>
                  <Input.TextArea
                    rows={4}
                    value={reasonInput}
                    onChange={(e) => {
                      const value = e.target.value
                      setReasonInput(value)
                      setLastEdited({ field_name: 'leave_reason', new_value: value })
                    }}
                    onBlur={() => {
                      if (reasonInput !== data.leaveReason) {
                        updateData({ type: 'UPDATE_FORM_DATA', payload: { leaveReason: reasonInput } })
                      }
                    }}
                    style={{ maxWidth: 400 }}
                  />
                </Form.Item>
              </Form>

              <Button
                type="primary"
                size="large"
                onClick={() => { void handleNext() }}
                disabled={!data.leaveType || !data.leaveStartDate || !data.leaveEndDate}
                loading={isLoading}
                style={{ background: '#7F00FF', borderColor: '#7F00FF', height: 48, paddingInline: 32 }}
              >
                <IntlMessages id="app.requestLeave" />
              </Button>
              <div style={{ marginTop: 12 }}>
                <Button
                  type="link"
                  size="large"
                  aria-label="Skip this step"
                  onClick={() => { void handleSkip() }}
                  className="onboarding-skip-step-link"
                  style={{ paddingInline: 0 }}
                >
                  <IntlMessages id="onboarding.leaveRequest.skip" />
                </Button>
              </div>
            </div>

            {/* Learn more: Leave Request video */}
            <Card className="onboarding-video-card" styles={{ body: { padding: 16 } }}>
              <Space align="start" size={16}>
                <a
                  href="https://www.youtube.com/watch?v=1_D3RC6Ghpg"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open leave request video"
                  onClick={() => {
                    if (authCompany) {
                      track('ONBOARDING_STEP_4_VIDEO_TUTORIAL_OPENED', { company_id: authCompany.id })
                    }
                  }}
                  style={{
                    display: 'block',
                    width: 96,
                    height: 54,
                    borderRadius: 8,
                    overflow: 'hidden',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                >
                  <img
                    src="https://img.youtube.com/vi/1_D3RC6Ghpg/hqdefault.jpg"
                    alt="Leave Request video thumbnail"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <PlayCircleOutlined
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      fontSize: 28,
                      color: '#ffffff',
                      textShadow: '0 2px 6px rgba(0,0,0,0.6)',
                    }}
                  />
                </a>
                <div>
                  <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                    <IntlMessages id="onboarding.leaveRequest.title" />
                  </Text>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    <IntlMessages id="onboarding.leaveRequest.howItWorks.title" />
                  </Text>
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => window.open('https://www.youtube.com/watch?v=1_D3RC6Ghpg', '_blank', 'noopener')}
                  >
                    <IntlMessages id="onboarding.department.about.learnMore" />
                  </Button>
                </div>
              </Space>
            </Card>
          </div>
        </div>

        <div className="step-right-section">
          <div style={{ maxWidth: 480, margin: '0 auto', width: '100%' }}>
            <Title level={3} style={{ fontSize: 24, marginBottom: 32 }}>
              <IntlMessages id="onboarding.leaveRequest.howItWorks.title" />
            </Title>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* Step 1 */}
              <div style={{
                background: '#f8f9fa',
                borderRadius: 12,
                padding: 20,
                border: '1px solid #e9ecef',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#7F00FF',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}>
                  1
                </div>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                    <IntlMessages id="onboarding.leaveRequest.howItWorks.notification.title" />
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ flexShrink: 0, transform: 'scale(1.1)' }}>
                      <LightningIcon />
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, marginBottom: '5px'}}>
                      <IntlMessages id="onboarding.leaveRequest.howItWorks.notification.description" />
                    </Text>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div style={{
                background: '#f8f9fa',
                borderRadius: 12,
                padding: 20,
                border: '1px solid #e9ecef',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#7F00FF',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}>
                  2
                </div>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                    <IntlMessages id="onboarding.leaveRequest.howItWorks.approval.title" />
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ flexShrink: 0, transform: 'scale(1.1)' }}>
                      <TimerIcon />
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, marginBottom: '5px' }}>
                      <IntlMessages id="onboarding.leaveRequest.howItWorks.approval.description" />
                    </Text>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div style={{
                background: '#f8f9fa',
                borderRadius: 12,
                padding: 20,
                border: '1px solid #e9ecef',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#7F00FF',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}>
                  3
                </div>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                    <IntlMessages id="onboarding.leaveRequest.howItWorks.notified.title" />
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ flexShrink: 0, transform: 'scale(1.1)' }}>
                      <LightningIcon />
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, marginBottom: '5px' }}>
                      <IntlMessages id="onboarding.leaveRequest.howItWorks.notified.description" />
                    </Text>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div style={{
                background: '#f8f9fa',
                borderRadius: 12,
                padding: 20,
                border: '1px solid #e9ecef',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 16,
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#7F00FF',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}>
                  4
                </div>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                    <IntlMessages id="onboarding.leaveRequest.howItWorks.update.title" />
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ flexShrink: 0, transform: 'scale(1.1)' }}>
                      <GearsIcon />
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, marginBottom: '5px' }}>
                      <IntlMessages id="onboarding.leaveRequest.howItWorks.update.description" />
                    </Text>
                  </div>
                </div>
              </div>
            </Space>
          </div>
        </div>
      </div>
    </div>
  )
}
